import React from "react";
import { Helmet } from "react-helmet-async";

const LAST_UPDATED = "February 2026";
const CONTACT_EMAIL = "sanguineaccent@gmail.com";
const SITE_URL = "https://sanguineaccent.com";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-playfair text-xl text-[#1c1917] mb-3">{title}</h2>
    <div className="text-stone-600 font-sourcesans text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const TermsAndConditions = () => (
  <>
    <Helmet>
      <title>Terms & Conditions — Sanguine Accent</title>
      <meta
        name="description"
        content="Terms and Conditions for Sanguine Accent. Please read before placing an order."
      />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${SITE_URL}/terms`} />
    </Helmet>

    <div className="min-h-screen bg-[#f5f3ea] pt-28 md:pt-44 pb-20 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-playfair text-3xl md:text-4xl text-[#1c1917] mb-2">
          Terms &amp; Conditions
        </h1>
        <p className="text-xs text-stone-400 font-sourcesans mb-10">
          Last updated: {LAST_UPDATED}
        </p>

        <Section title="About Sanguine Accent">
          <p>
            Sanguine Accent is an independent handmade shop run by Anna Gapyuk,
            selling handmade string art greeting cards. By using this website or
            placing an order, you agree to these terms.
          </p>
        </Section>

        <Section title="Products">
          <p>
            All cards are handmade using 100% tree-free cotton cardstock and
            premium DMC threads. Because each card is made individually, slight
            variations in thread placement and color are normal — not defects.
            Every card is unique.
          </p>
          <p>
            Cards are A2 size (4.25" × 5.5"), blank inside for your personal
            message, and come with a matching envelope. They can also be framed
            as keepsake art pieces.
          </p>
          <p>
            Product images are as accurate as possible but may vary slightly
            depending on your screen.
          </p>
        </Section>

        <Section title="Orders and payment">
          <p>
            Orders are confirmed once payment is successfully processed through
            Stripe. You can view your order history and status in your account
            under Orders. Prices are in US dollars and may change without
            notice — the price shown at time of purchase is final.
          </p>
          <p>
            Stripe handles all payment processing. We never see or store your
            card details. Your card statement will show the charge from Sanguine
            Accent.
          </p>
          <p>
            We reserve the right to cancel any order if an item becomes
            unavailable, in which case a full refund will be issued.
          </p>
        </Section>

        <Section title="Shipping">
          <p>
            We ship from the United States. Please allow 2–3 business days for
            us to prepare your handmade card. Once shipped, delivery times vary
            by destination and carrier and are not guaranteed.
          </p>
          <p>
            For international orders, please contact us before ordering so we
            can confirm shipping details and costs for your location.
          </p>
          <p>
            We are not responsible for delays caused by carriers, customs, or
            circumstances outside our control. If your order is lost or
            significantly delayed, contact us and we will do our best to help.
          </p>
        </Section>

        <Section title="Returns and refunds">
          <p>
            Because each card is handmade to order, we do not accept returns.
            If your order arrives damaged or incorrect, contact us within 7 days
            of delivery with a photo and your order number — we will send a
            replacement or issue a full refund.
          </p>
          <p>
            Please avoid water contact to preserve your card. If you plan to
            frame it, handle it by the edges.
          </p>
          <p>
            Email{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#BC3F4A] underline"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            with your order number and photos of the issue.
          </p>
        </Section>

        <Section title="Ukraine support">
          <p>
            70% of every purchase is sent directly to individuals personally
            known to the owner who are providing humanitarian support in Ukraine
            — including food, clothing, and communication devices. The
            remaining 30% covers materials, website hosting, and transaction
            fees.
          </p>
          <p>
            These are personal transfers, not donations through a registered
            nonprofit or charity. We do not issue tax receipts for this
            portion.
          </p>
        </Section>

        <Section title="Accounts">
          <p>
            You are responsible for keeping your account credentials secure. We
            use HTTP-only secure cookies for authentication — your session is
            never stored in your browser's local storage. Passwords are hashed
            and never stored in plain text.
          </p>
          <p>
            You may request deletion of your account at any time by emailing
            us.
          </p>
        </Section>

        <Section title="Custom orders">
          <p>
            We welcome custom orders. Please reach out to discuss your design
            ideas and we will work with you to bring your vision to life.
            Contact us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#BC3F4A] underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="Intellectual property">
          <p>
            All card designs, photography, and written content on this site are
            created by Anna Gapyuk and may not be reproduced, copied, or used
            commercially without written permission.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            Sanguine Accent is not liable for indirect, incidental, or
            consequential damages arising from use of this site or purchase of
            our products. Our liability is limited to the value of the order in
            question.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these terms from time to time. The date at the top
            reflects the most recent update. Continued use of the site after
            changes constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions? Email{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#BC3F4A] underline"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>
      </div>
    </div>
  </>
);

export default TermsAndConditions;